import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { MenuIcon, type MenuIconName } from '../reports/MenuIcons.js';
import type { Folder } from '../../api/folders.js';
import type { ReportSummary } from '../../api/reports.js';
import type { ReportTemplateSummary } from '../../api/types.js';
import {
  ChevronDown,
  ChevronRight,
  FolderIcon,
  KebabIcon,
  ReportIcon as ReportLeafIcon,
} from '../../components/icons.js';

/**
 * ReportsFolderTree — explorer tree where the leaves are Report
 * (instance) rows. Structurally mirrors pages/reports/FolderTree.tsx
 * (used for templates) but with report-specific actions including
 * "Run now" and "Duplicate…".
 */
export interface ReportsFolderTreeProps {
  folders: ReadonlyArray<Folder>;
  reports: ReadonlyArray<ReportSummary>;
  selectedReportId: string | null;
  onSelectReport(id: string): void;
  expanded: ReadonlySet<string>;
  onToggle(id: string): void;
  searchQuery?: string;

  onRenameFolder(folder: Folder): void;
  onMoveFolder(folder: Folder): void;
  onDeleteFolder(folder: Folder): void;
  onNewFolder(parent: Folder | null): void;

  onRenameReport(report: ReportSummary): void;
  onMoveReport(report: ReportSummary): void;
  onCloneReport(report: ReportSummary): void;
  onDeleteReport(report: ReportSummary): void;
  onRunReport(report: ReportSummary): void;
  /** Reorder a report up or down within its folder. */
  onReorderReport(report: ReportSummary, direction: 'up' | 'down'): void;
  /** Reorder a folder up or down within its parent. */
  onReorderFolder(folder: Folder, direction: 'up' | 'down'): void;

  /** Drag-and-drop moves. folderId/parentId === null means top level. */
  onDropReportToFolder(reportId: string, folderId: string | null): void;
  onDropFolderToFolder(folderId: string, parentId: string | null): void;

  /** Optional template id → template summary lookup. When present, the
   *  leaf tooltip shows the bound template's description so operators
   *  hovering a report instance see what the underlying template does. */
  templatesById?: Map<string, ReportTemplateSummary>;

  /** Consumer (embed) mode: hide authoring affordances. Folder rows lose
   *  their kebab; report rows keep an always-visible kebab with just the
   *  consumer-relevant "Open" action. */
  consumerMode?: boolean;
}

// Module-scoped drag state — there's a single tree instance, and this is
// more reliable across browsers than round-tripping through dataTransfer.
let dragState: { kind: 'report' | 'folder'; id: string } | null = null;

const DROP_STYLE: CSSProperties = {
  outline: '2px solid var(--color-accent, #6366f1)',
  outlineOffset: '-2px',
  background: 'var(--color-accent-softer, rgba(99,102,241,0.08))',
  borderRadius: 'var(--radius-sm, 4px)',
};

interface FolderNode {
  kind: 'folder';
  folder: Folder;
  children: TreeNode[];
  directCount: number;
  subtreeCount: number;
}
interface ReportNode {
  kind: 'report';
  report: ReportSummary;
}
type TreeNode = FolderNode | ReportNode;

export function ReportsFolderTree(props: ReportsFolderTreeProps): JSX.Element {
  const {
    folders,
    reports,
    selectedReportId,
    onSelectReport,
    expanded,
    onToggle,
    searchQuery,
    consumerMode = false,
    onRenameFolder,
    onMoveFolder,
    onDeleteFolder,
    onNewFolder,
    onReorderFolder,
    onRenameReport,
    onMoveReport,
    onCloneReport,
    onDeleteReport,
    onRunReport,
    onReorderReport,
    onDropReportToFolder,
    onDropFolderToFolder,
  } = props;

  const tree = useMemo(
    () => buildTree(folders, reports, searchQuery ?? ''),
    [folders, reports, searchQuery],
  );
  const forceExpanded = useMemo<ReadonlySet<string>>(
    () => computeAutoExpand(folders, reports, searchQuery ?? ''),
    [folders, reports, searchQuery],
  );

  // Drop onto the root = move to top level.
  const [rootDrop, setRootDrop] = useState(false);

  return (
    <div className="folder-tree" role="tree">
      {/* Authoring shows an "All reports" root row (drop-to-top-level target +
          new-folder menu). In the consumer embed the enclosing section header
          is the "Reports" row and carries the new-folder action, so this root
          row is redundant and hidden — the folders/reports sit indented under
          the header instead. */}
      {!consumerMode && (
        <div
          className="folder-tree-row folder-tree-row--root"
          style={rootDrop ? DROP_STYLE : undefined}
          onDragOver={(e) => {
            if (!dragState) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (!rootDrop) setRootDrop(true);
          }}
          onDragLeave={() => setRootDrop(false)}
          onDrop={(e) => {
            e.preventDefault();
            setRootDrop(false);
            const d = dragState;
            dragState = null;
            if (!d) return;
            if (d.kind === 'report') onDropReportToFolder(d.id, null);
            else onDropFolderToFolder(d.id, null);
          }}
        >
          <span className="folder-tree-chevron folder-tree-chevron--static">
            <FolderIcon open />
          </span>
          <span className="folder-tree-label">
            <span className="folder-tree-name">All reports</span>
            <span className="folder-tree-count">{reports.length}</span>
          </span>
          <RowMenu
            items={[
              { label: 'New folder at top', icon: 'folder-plus', action: () => onNewFolder(null) },
            ]}
          />
        </div>
      )}
      <ul className="folder-tree-list">
        {tree.rootNodes.map((n) => (
          <TreeRow
            key={keyFor(n)}
            node={n}
            depth={0}
            consumerMode={consumerMode}
            selectedReportId={selectedReportId}
            onSelectReport={onSelectReport}
            expanded={expanded}
            forceExpanded={forceExpanded}
            onToggle={onToggle}
            onRenameFolder={onRenameFolder}
            onMoveFolder={onMoveFolder}
            onDeleteFolder={onDeleteFolder}
            onNewFolder={onNewFolder}
            onReorderFolder={onReorderFolder}
            onRenameReport={onRenameReport}
            onMoveReport={onMoveReport}
            onCloneReport={onCloneReport}
            onDeleteReport={onDeleteReport}
            onRunReport={onRunReport}
            onReorderReport={onReorderReport}
            onDropReportToFolder={onDropReportToFolder}
            onDropFolderToFolder={onDropFolderToFolder}
          />
        ))}
      </ul>
    </div>
  );
}

interface TreeRowProps extends Omit<ReportsFolderTreeProps, 'folders' | 'reports' | 'searchQuery'> {
  node: TreeNode;
  depth: number;
  forceExpanded: ReadonlySet<string>;
}

function TreeRow(p: TreeRowProps): JSX.Element {
  return p.node.kind === 'report' ? (
    <ReportLeaf {...p} node={p.node} />
  ) : (
    <FolderRow {...p} node={p.node} />
  );
}

function FolderRow({
  node,
  depth,
  expanded,
  forceExpanded,
  onToggle,
  consumerMode = false,
  selectedReportId,
  onSelectReport,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
  onNewFolder,
  onReorderFolder,
  onRenameReport,
  onMoveReport,
  onCloneReport,
  onDeleteReport,
  onRunReport,
  onReorderReport,
  onDropReportToFolder,
  onDropFolderToFolder,
}: TreeRowProps & { node: FolderNode }): JSX.Element {
  const f = node.folder;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(f.id) || forceExpanded.has(f.id);
  const indent: CSSProperties = { paddingLeft: `${8 + depth * 14}px` };
  const [dropHover, setDropHover] = useState(false);
  if (node.subtreeCount === 0) return <></>;
  return (
    <li role="treeitem" aria-expanded={hasChildren ? isOpen : undefined}>
      <div
        className="folder-tree-row"
        style={dropHover ? { ...indent, ...DROP_STYLE } : indent}
        draggable
        onDragStart={(e) => {
          dragState = { kind: 'folder', id: f.id };
          e.dataTransfer.effectAllowed = 'move';
          try {
            e.dataTransfer.setData('text/plain', f.id);
          } catch {
            /* noop */
          }
        }}
        onDragEnd={() => {
          dragState = null;
          setDropHover(false);
        }}
        onDragOver={(e) => {
          if (!dragState) return;
          if (dragState.kind === 'folder' && dragState.id === f.id) return; // self
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (!dropHover) setDropHover(true);
        }}
        onDragLeave={() => {
          if (dropHover) setDropHover(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDropHover(false);
          const d = dragState;
          dragState = null;
          if (!d) return;
          if (d.kind === 'report') onDropReportToFolder(d.id, f.id);
          else if (d.id !== f.id) onDropFolderToFolder(d.id, f.id);
        }}
      >
        <button
          type="button"
          className="folder-tree-chevron"
          onClick={() => hasChildren && onToggle(f.id)}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            isOpen ? (
              <ChevronDown />
            ) : (
              <ChevronRight />
            )
          ) : (
            <span className="folder-tree-dot">·</span>
          )}
        </button>
        <button
          type="button"
          className="folder-tree-label folder-tree-label--folder"
          onClick={() => hasChildren && onToggle(f.id)}
          title={f.name}
        >
          <span className="folder-tree-icon">
            <FolderIcon open={isOpen} />
          </span>
          <span className="folder-tree-name">{f.name}</span>
          <span className="folder-tree-count">{node.subtreeCount}</span>
        </button>
        <RowMenu
          items={
            consumerMode
              ? [
                  { label: 'New folder inside', icon: 'folder-plus', action: () => onNewFolder(f) },
                  { label: 'Rename…', icon: 'rename', action: () => onRenameFolder(f) },
                  {
                    label: 'Delete folder',
                    icon: 'delete',
                    action: () => onDeleteFolder(f),
                    destructive: true,
                  },
                ]
              : [
                  { label: 'New folder inside', icon: 'folder-plus', action: () => onNewFolder(f) },
                  { label: 'Move up', icon: 'move', action: () => onReorderFolder(f, 'up') },
                  { label: 'Move down', icon: 'move', action: () => onReorderFolder(f, 'down') },
                  { label: 'Rename…', icon: 'rename', action: () => onRenameFolder(f) },
                  { label: 'Move…', icon: 'move', action: () => onMoveFolder(f) },
                  {
                    label: 'Delete folder',
                    icon: 'delete',
                    action: () => onDeleteFolder(f),
                    destructive: true,
                  },
                ]
          }
        />
      </div>
      {isOpen && hasChildren && (
        <ul className="folder-tree-list">
          {node.children.map((c) => (
            <TreeRow
              key={keyFor(c)}
              node={c}
              depth={depth + 1}
              consumerMode={consumerMode}
              expanded={expanded}
              forceExpanded={forceExpanded}
              onToggle={onToggle}
              selectedReportId={selectedReportId}
              onSelectReport={onSelectReport}
              onRenameFolder={onRenameFolder}
              onMoveFolder={onMoveFolder}
              onDeleteFolder={onDeleteFolder}
              onNewFolder={onNewFolder}
              onReorderFolder={onReorderFolder}
              onRenameReport={onRenameReport}
              onMoveReport={onMoveReport}
              onCloneReport={onCloneReport}
              onDeleteReport={onDeleteReport}
              onRunReport={onRunReport}
              onReorderReport={onReorderReport}
              onDropReportToFolder={onDropReportToFolder}
              onDropFolderToFolder={onDropFolderToFolder}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function ReportLeaf({
  node,
  depth,
  consumerMode,
  selectedReportId,
  onSelectReport,
  onRenameReport,
  onMoveReport,
  onCloneReport,
  onDeleteReport,
  onRunReport,
  onReorderReport,
  templatesById,
}: TreeRowProps & { node: ReportNode }): JSX.Element {
  const r = node.report;
  const isSelected = selectedReportId === r.id;
  const indent: CSSProperties = { paddingLeft: `${24 + depth * 14}px` };
  return (
    <li role="treeitem" aria-selected={isSelected}>
      <div
        className={`folder-tree-row folder-tree-row--leaf ${isSelected ? 'folder-tree-row--selected' : ''}`}
        style={indent}
        draggable
        onDragStart={(e) => {
          dragState = { kind: 'report', id: r.id };
          e.dataTransfer.effectAllowed = 'move';
          try {
            e.dataTransfer.setData('text/plain', r.id);
          } catch {
            /* noop */
          }
        }}
        onDragEnd={() => {
          dragState = null;
        }}
      >
        <button
          type="button"
          className="folder-tree-label folder-tree-label--template"
          onClick={() => onSelectReport(r.id)}
          title={(() => {
            const tplDesc = templatesById?.get(r.templateId)?.description;
            return tplDesc ? `${r.name}\n\n${tplDesc}` : r.name;
          })()}
        >
          <span className="folder-tree-icon folder-tree-icon--template">
            <ReportLeafIcon />
          </span>
          <span className="folder-tree-name">{r.name}</span>
        </button>
        <RowMenu
          items={
            consumerMode
              ? [
                  { label: 'Open', icon: 'open', action: () => onSelectReport(r.id) },
                  { label: 'Rename…', icon: 'rename', action: () => onRenameReport(r) },
                  { label: 'Duplicate…', icon: 'clone', action: () => onCloneReport(r) },
                  {
                    label: 'Delete report',
                    icon: 'delete',
                    action: () => onDeleteReport(r),
                    destructive: true,
                  },
                ]
              : [
                  { label: 'Open', icon: 'open', action: () => onSelectReport(r.id) },
                  { label: 'Run now', icon: 'play', action: () => onRunReport(r) },
                  { label: 'Move up', icon: 'move', action: () => onReorderReport(r, 'up') },
                  { label: 'Move down', icon: 'move', action: () => onReorderReport(r, 'down') },
                  { label: 'Rename…', icon: 'rename', action: () => onRenameReport(r) },
                  { label: 'Move to…', icon: 'move', action: () => onMoveReport(r) },
                  { label: 'Duplicate…', icon: 'clone', action: () => onCloneReport(r) },
                  {
                    label: 'Delete report',
                    icon: 'delete',
                    action: () => onDeleteReport(r),
                    destructive: true,
                  },
                ]
          }
        />
      </div>
    </li>
  );
}

export interface MenuItem {
  label: string;
  action(): void;
  destructive?: boolean;
  icon?: MenuIconName;
}
/**
 * Hover-kebab menu — portaled to document.body to escape the
 * scrollable left tree's overflow clipping (CSAGE-94).
 *
 * Exported (CSAGE-353) so ConsumerReportsList can give My Reports rows the same
 * actions instead of a third hand-rolled copy. The templates tree
 * (pages/reports/FolderTree.tsx) still carries its own twin and can migrate to
 * this one when next touched.
 */
export function RowMenu({ items }: { items: ReadonlyArray<MenuItem> }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const popWidth = 160;
    // CSAGE-273's fix, applied here too: portaling escapes the tree's overflow
    // clipping but not the VIEWPORT edge, so a menu opened on the last visible
    // row still had its items cut off. Flip above the trigger when there isn't
    // room below. popRef has no height on the first pass, so fall back to an
    // estimate from the item count rather than mis-measuring at 0.
    const popH = popRef.current?.offsetHeight || items.length * 32 + 8;
    const GAP = 2;
    const roomBelow = window.innerHeight - r.bottom;
    const flipUp = roomBelow < popH + GAP && r.top > popH + GAP;
    const top = flipUp ? r.top - popH - GAP : r.bottom + GAP;
    setPos({
      top: Math.max(GAP, Math.min(top, window.innerHeight - popH - GAP)),
      left: Math.max(8, r.right - popWidth),
    });
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      const t = e.target as globalThis.Node | null;
      if (t && popRef.current?.contains(t)) return;
      if (t && btnRef.current?.contains(t)) return;
      close();
    }
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') close();
    }
    function onLayoutChange(): void {
      close();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onLayoutChange, true);
    window.addEventListener('resize', onLayoutChange);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onLayoutChange, true);
      window.removeEventListener('resize', onLayoutChange);
    };
  }, [open, close]);

  const popover =
    open && pos
      ? createPortal(
          <div
            ref={popRef}
            className="folder-tree-menu-pop folder-tree-menu-pop--portaled"
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
          >
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                className={`folder-tree-menu-item ${it.destructive ? 'folder-tree-menu-item--danger' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  close();
                  it.action();
                }}
              >
                <span className="folder-tree-menu-icon">
                  {it.icon ? <MenuIcon name={it.icon} /> : null}
                </span>
                <span>{it.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="folder-tree-menu">
      <button
        ref={btnRef}
        type="button"
        className="folder-tree-menu-trigger"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label="Actions"
      >
        <KebabIcon />
      </button>
      {popover}
    </div>
  );
}

function buildTree(
  folders: ReadonlyArray<Folder>,
  reports: ReadonlyArray<ReportSummary>,
  searchQuery: string,
): { rootNodes: TreeNode[] } {
  const q = searchQuery.trim().toLowerCase();
  const matches = (s: string): boolean => !q || s.toLowerCase().includes(q);
  const folderNodes = new Map<string, FolderNode>();
  for (const f of folders)
    folderNodes.set(f.id, {
      kind: 'folder',
      folder: f,
      children: [],
      directCount: 0,
      subtreeCount: 0,
    });
  const rootFolders: FolderNode[] = [];
  for (const f of folders) {
    const node = folderNodes.get(f.id)!;
    if (f.parentId && folderNodes.has(f.parentId)) folderNodes.get(f.parentId)!.children.push(node);
    else rootFolders.push(node);
  }
  const topLevel: ReportNode[] = [];
  for (const r of reports) {
    if (!matches(r.name)) continue;
    const leaf: ReportNode = { kind: 'report', report: r };
    if (r.folderId && folderNodes.has(r.folderId)) {
      const host = folderNodes.get(r.folderId)!;
      host.children.push(leaf);
      host.directCount += 1;
    } else topLevel.push(leaf);
  }
  const computeSubtree = (n: FolderNode): number => {
    let total = n.directCount;
    for (const c of n.children) if (c.kind === 'folder') total += computeSubtree(c);
    n.subtreeCount = total;
    return total;
  };
  for (const r of rootFolders) computeSubtree(r);
  const sortSiblings = (arr: TreeNode[]): void => {
    arr.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
      if (a.kind === 'folder' && b.kind === 'folder') {
        if (a.subtreeCount !== b.subtreeCount) return b.subtreeCount - a.subtreeCount;
        return a.folder.name.localeCompare(b.folder.name);
      }
      if (a.kind === 'report' && b.kind === 'report')
        return a.report.name.localeCompare(b.report.name);
      return 0;
    });
  };
  const sortAll = (n: FolderNode): void => {
    sortSiblings(n.children);
    for (const c of n.children) if (c.kind === 'folder') sortAll(c);
  };
  for (const r of rootFolders) sortAll(r);
  const rootNodes: TreeNode[] = [...rootFolders, ...topLevel];
  sortSiblings(rootNodes);
  return { rootNodes };
}

function computeAutoExpand(
  folders: ReadonlyArray<Folder>,
  reports: ReadonlyArray<ReportSummary>,
  searchQuery: string,
): ReadonlySet<string> {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return new Set();
  const byId = new Map<string, Folder>();
  for (const f of folders) byId.set(f.id, f);
  const ancestors = (id: string | null): string[] => {
    const out: string[] = [];
    let cursor: string | null = id;
    while (cursor) {
      out.push(cursor);
      const f = byId.get(cursor);
      cursor = f ? f.parentId : null;
    }
    return out;
  };
  const open = new Set<string>();
  for (const r of reports)
    if (r.name.toLowerCase().includes(q)) for (const a of ancestors(r.folderId)) open.add(a);
  for (const f of folders)
    if (f.name.toLowerCase().includes(q)) for (const a of ancestors(f.id)) open.add(a);
  return open;
}

function keyFor(n: TreeNode): string {
  return n.kind === 'folder' ? `f:${n.folder.id}` : `r:${n.report.id}`;
}
